import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';
import WhyChooseUs from '../components/WhyChooseUs';
import ServicesSection from '../components/ServicesSection';
import ProcessSection from '../components/ProcessSection';
import TestimonialsSection from '../components/TestimonialsSection';
import CTASection from '../components/CTASection';
import StatsBar from '../components/StatsBar';

const TITLES = [
  'Maximum Refund.',
  'Fast Filing.',
  'Trusted Service.',
];

const Home = () => {
  const [titleIndex, setTitleIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const particleRef = useRef(null);

  // Cycling text transition
  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setTitleIndex((prev) => (prev + 1) % TITLES.length);
        setFade(true);
      }, 400);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  // Floating particles
  useEffect(() => {
    const canvas = particleRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const particles = Array.from({ length: 38 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 2.5 + 0.5,
      dx: (Math.random() - 0.5) * 0.35,
      dy: (Math.random() - 0.5) * 0.35,
      alpha: Math.random() * 0.5 + 0.1,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212,160,23,${p.alpha})`;
        ctx.fill();
        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  const stats = [
    { num: '50', label: 'States Served' },
    { num: '24h', label: 'Fast Turnaround' },
    { num: '100%', label: 'IRS Compliant' },
    { num: 'Max', label: 'Refund Guarantee' },
  ];

  return (
    <>
      {/* ===== HERO ===== */}
      <section className="hero">
        {/* Particle canvas */}
        <canvas ref={particleRef} className="hero-particles" />

        {/* Animated gradient orbs */}
        <div className="hero-orb hero-orb-1" />
        <div className="hero-orb hero-orb-2" />
        <div className="hero-orb hero-orb-3" />

        {/* SVG Wave at bottom */}
        <div className="hero-wave">
          <svg viewBox="0 0 1440 120" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0,60 C240,120 480,0 720,60 C960,120 1200,0 1440,60 L1440,120 L0,120 Z"
              fill="white"
              opacity="1"
            />
            <path
              d="M0,80 C200,30 400,110 600,70 C800,30 1000,100 1200,60 C1300,40 1370,80 1440,70 L1440,120 L0,120 Z"
              fill="white"
              opacity="0.5"
            />
          </svg>
        </div>

        <div className="container">
          <div className="hero-grid">
            {/* LEFT: Content */}
            <div className="hero-content">
              <div className="hero-badge bounce-in">
                🏆 Trusted Tax Professionals — Serving All 50 States
              </div>

              {/* Cycling animated title */}
              <h1 className="hero-title slide-in-left">
                <span className={`hero-cycle-text ${fade ? 'text-visible' : 'text-hidden'}`}>
                  {TITLES[titleIndex].includes('Refund') ? (
                    <><span className="text-gold">{TITLES[titleIndex]}</span></>
                  ) : (
                    TITLES[titleIndex]
                  )}
                </span>
                <br />
                <span className="hero-sub-title">
                  We Maximize Your Refund.
                </span>
              </h1>

              <p className="hero-tagline slide-in-left" style={{ animationDelay: '0.35s' }}>
                "We Don't Just File Taxes — We Maximize Your Refund"
              </p>
              <p className="hero-desc slide-in-left" style={{ animationDelay: '0.5s' }}>
                A-Mobile Tax Services provides fast, accurate, and secure tax filing across all 50 states
                — from W2 to self-employed and business taxes.
              </p>

              <div className="hero-buttons slide-in-left" style={{ animationDelay: '0.65s' }}>
                <Link to="/contact" className="btn btn-gold btn-bounce">📲 Book Appointment</Link>
                <Link to="/contact" className="btn btn-outline-white">📤 Upload Documents</Link>
                <Link to="/contact" className="btn btn-outline-gold">💬 Get Free Quote</Link>
              </div>
            </div>

            {/* RIGHT: Visual */}
            <div className="hero-visual fade-in-right">
              <div className="hero-card float-card">
                <div className="hero-card-header">
                  <div className="hero-card-icon">🏢</div>
                  <h3>Business &amp; Personal Tax</h3>
                </div>
                <p>LLC, S-Corps, Sole Proprietors, W-2 employees, 1099 contractors — we handle every tax situation with precision.</p>
              </div>

              <div className="hero-stats-grid">
                {stats.map((s, i) => (
                  <div
                    className="hero-stat bounce-stat"
                    key={s.label}
                    style={{ animationDelay: `${0.3 + i * 0.12}s` }}
                  >
                    <div className="hero-stat-num">{s.num}</div>
                    <div className="hero-stat-label">{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Floating badge */}
              <div className="hero-floating-badge">
                <span className="badge-dot" />
                <span>IRS Authorized E-File Provider</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sections */}
      <WhyChooseUs />
      <StatsBar />
      <ServicesSection />
      <ProcessSection />
      <TestimonialsSection />
      <CTASection />
    </>
  );
};

export default Home;
